import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import api, { fileUrl, API } from "@/lib/api";

const thumbUrl = (id) => `${API}/media/${id}/thumbnail`;

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/media", { params: { kind: "video" } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-black min-h-screen anim-fadeup" data-testid="portfolio-page">
      <section className="border-b border-neutral-900 py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-4">
            01 / REELS
          </div>
          <h1 className="font-bodoni font-black text-6xl md:text-8xl lg:text-9xl leading-[0.85]">
            Motion <span className="italic">archive</span>.
          </h1>
          <p className="mt-6 max-w-2xl font-mono text-sm text-neutral-400">
            A rolling catalog of cinematic captures — fight films, training documentaries,
            brand campaigns. Hover to preview. Tap to enter the cut.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="font-mono text-xs text-neutral-600 tracking-widest cursor-blink">
              FETCHING TRANSMISSION
            </div>
          ) : items.length === 0 ? (
            <div className="border border-neutral-900 p-20 text-center" data-testid="reels-empty">
              <div className="font-mono text-[10px] text-neutral-600 tracking-widest mb-3">
                NO TRANSMISSIONS YET
              </div>
              <p className="font-bodoni italic text-3xl text-neutral-500">
                The reels are being processed.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-4 md:gap-6">
              {items.map((m, i) => {
                // Bento layout pattern
                const isLarge = i % 6 === 0 || i % 6 === 3;
                return (
                  <motion.button
                    key={m.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (i % 6) * 0.05 }}
                    onClick={() => setActive(m)}
                    data-testid={`reel-card-${m.id}`}
                    className={`relative group overflow-hidden bg-neutral-950 border border-neutral-900 hover:border-white transition-all aspect-video text-left ${
                      isLarge ? "col-span-12 md:col-span-8" : "col-span-12 md:col-span-4"
                    }`}
                  >
                    {m.thumbnail_path && (
                      <img
                        src={thumbUrl(m.id)}
                        alt={m.title}
                        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:opacity-0 transition-opacity duration-300"
                        loading="lazy"
                      />
                    )}
                    <video
                      src={fileUrl(m.id)}
                      preload={m.thumbnail_path ? "none" : "metadata"}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      muted
                      loop
                      playsInline
                      onMouseEnter={(e) => e.currentTarget.play()}
                      onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent pointer-events-none" />
                    <div className="absolute top-4 left-4 right-4 flex justify-between font-mono text-[10px] tracking-[0.3em] text-neutral-300">
                      <span>REEL.{String(i + 1).padStart(3, "0")}</span>
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity">▶ PLAY</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="font-bodoni text-3xl mb-1 leading-tight">{m.title}</div>
                      {m.description && (
                        <div className="font-mono text-xs text-neutral-400 line-clamp-2">{m.description}</div>
                      )}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full border border-white/40 backdrop-blur-sm flex items-center justify-center bg-black/30">
                        <Play size={20} className="text-white ml-1" />
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="reel-lightbox"
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setActive(null)}
              className="absolute top-6 right-6 text-white hover:text-neutral-400"
            >
              <X size={28} />
            </button>
            <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
              <video
                src={fileUrl(active.id)}
                className="w-full aspect-video bg-black"
                controls
                autoPlay
              />
              <div className="mt-6 flex justify-between items-baseline">
                <div>
                  <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-1">REEL</div>
                  <div className="font-bodoni text-3xl">{active.title}</div>
                </div>
                <div className="font-mono text-[10px] text-neutral-500">JITSUENT//{active.id.slice(0, 6).toUpperCase()}</div>
              </div>
              {active.description && (
                <p className="mt-3 font-mono text-sm text-neutral-400 max-w-2xl">{active.description}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
